import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import ChatMessage from './ChatMessage';
import { Send } from 'lucide-react';

const AnonymousChatTab = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { socket } = useSocket();

  const normalizeHistory = (history) => {
    if (Array.isArray(history)) return history;
    if (Array.isArray(history?.messages)) return history.messages;
    return [];
  };

  useEffect(() => {
    if (!socket) return;

    const requestHistory = () => {
      socket.emit('anonymous:load_history', { limit: 50 });
    };

    const handleHistory = (history) => {
      setMessages(normalizeHistory(history));
      scrollToBottom();
    };

    const handleChatMessage = (data) => {
      if (!data) return;

      setMessages((prev) => {
        const nextId = data.id || data.message_id || `${data.timestamp || data.created_at || data.message}`;
        if (
          prev.some((msg) => {
            const currentId = msg.id || msg.message_id || `${msg.timestamp || msg.created_at || msg.message}`;
            return String(currentId) === String(nextId);
          })
        ) {
          return prev;
        }

        return [...prev, data];
      });
      scrollToBottom();
    };

    const handleError = (data) => {
      console.error('Anonymous chat error:', data?.message);
    };

    if (socket.connected) {
      requestHistory();
    }

    socket.on('connect', requestHistory);
    socket.on('anonymous:history', handleHistory);
    socket.on('anonymous:chat_message', handleChatMessage);
    socket.on('anonymous:error', handleError);

    return () => {
      socket.off('connect', requestHistory);
      socket.off('anonymous:history', handleHistory);
      socket.off('anonymous:chat_message', handleChatMessage);
      socket.off('anonymous:error', handleError);
    };
  }, [socket]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !socket) return;
    
    socket.emit('anonymous:chat_message', {
      message: inputMessage.trim()
    });
    
    setInputMessage('');
  };

  return (
    <div className="tab-panel">
      <div className="tab-info">
        <span className="tab-title">👤 Chat Anónimo</span>
        <span className="tab-subtitle">Sin revelar identidad</span>
      </div>
      
      <div className="messages-area">
        {messages.length === 0 ? (
          <div className="empty-messages">
            <p>No hay mensajes aún</p>
            <p className="text-sm">¡Escribe algo anónimamente!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <ChatMessage
              key={idx}
              message={msg.message}
              timestamp={msg.timestamp || msg.created_at}
              showUsername={false}
              isAnonymous={true}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form className="message-input" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Mensaje anónimo..."
          maxLength={500}
        />
        <button type="submit" disabled={!inputMessage.trim()}>
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default AnonymousChatTab;
